import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceProject } from "../../common/project";
import { reduceTask } from "../../common/task";
import { reduceZone } from "../../common/zone";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { matchQuery } from "./utils";
import { reduceMilestone } from "../../common/milestone";

const initialState: State = [];

function handleRequest(
  state: State,
  query: Boundaries["search"]["request"],
): State {
  const index = state.findIndex((req) => matchQuery(query, req.query));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToPending(),
          results: query.start === 0 ? [] : state[index].results,
          query,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToPending(),
          results: query.start === 0 ? [] : state[index].results,
          query,
          canSearch: true,
        },
      ];
}

function handleSuccess(
  state: State,
  { query, results }: Boundaries["search"]["success"],
): State {
  const index = state.findIndex((req) => matchQuery(query, req.query));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          results:
            query.start === 0 ? results : [...state[index].results, ...results],
          query: { ...query, start: query.start + 1 },
          canSearch: results.length >= query.limit,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          results: query.start === 0 ? [] : results,
          request: setRequestStateToSuccess(),
          query: { ...query, start: query.start + 1 },
          canSearch: results.length >= query.limit,
        },
      ];
}

function handleFailure(
  state: State,
  query: Boundaries["search"]["failure"],
): State {
  const index = state.findIndex((req) => matchQuery(query, req.query));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          results: [],
          request: setRequestStateToFailure(),
          query,
          canSearch: true,
        },
      ];
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    results: req.results.map((result) => ({
      ...result,
      args:
        result.type === "project"
          ? reduceProject(result.args, event)
          : result.type === "task"
            ? {
                ...reduceTask(result.args, event),
                project: reduceProject(result.args.project, event),
                zone: reduceZone(result.args.zone, event),
              }
            : result.type === "milestone"
              ? {
                  ...reduceMilestone(result.args, event),
                  project: reduceProject(result.args.project, event),
                  zone: reduceZone(result.args.zone, event),
                }
              : (result as any).args,
    })),
  }));
}

const reduceState = createStateReducer(
  initialState,
  {
    [events.searchRequested]: handleRequest,
    [events.searchSucceeded]: handleSuccess,
    [events.searchFailed]: handleFailure,
  },
  propagateEvent,
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
